import React from "react";
import { Helmet } from "react-helmet";
import StaticPageStyle from "./StaticPageStyle";

const PrivacyPolicy = () => {
  return (
    <React.Fragment>
      <Helmet>
        <title>Privacy Policy — Propeller PH</title>
        <meta
          name="description"
          content="How Propeller PH collects, uses, and protects your personal information, including our use of cookies, analytics, and advertising services."
        />
        <link rel="canonical" href="https://www.propeller.ph/privacy-policy" />
      </Helmet>
      <StaticPageStyle>
        <div className="container">
          <h1>Privacy Policy</h1>
          <p className="page-meta">Last updated: July 10, 2026</p>

          <p>
            This Privacy Policy explains how Propeller Realty Investments
            ("Propeller PH", "we", "us", or "our") collects, uses, and protects
            information about visitors to{" "}
            <a href="https://www.propeller.ph">www.propeller.ph</a> (the
            "Site"). We are a Philippine real estate agency and we process
            personal data in accordance with Republic Act No. 10173, the Data
            Privacy Act of 2012, and its implementing rules and regulations.
          </p>

          <h2>Information we collect</h2>
          <h3>Information you provide</h3>
          <p>
            When you contact us or submit an inquiry — for example, through an
            inquiry form on a property listing, our contact form, or our
            property listing form — we collect the details you choose to share,
            such as your name, email address, phone number, and the content of
            your message. We use this information to respond to your inquiry,
            arrange viewings, and provide our brokerage services.
          </p>
          <h3>Information collected automatically</h3>
          <p>
            When you browse the Site, we and our service providers
            automatically collect certain technical information, including your
            IP address, browser type, device type, pages visited, and the date
            and time of your visit. This is collected through cookies and
            similar technologies as described below.
          </p>

          <h2>Cookies and similar technologies</h2>
          <p>
            Cookies are small text files stored on your device that help
            websites function and provide usage insights. We use cookies and
            similar technologies for the following purposes:
          </p>
          <ul>
            <li>
              <strong>Analytics.</strong> We use Google Analytics to understand
              how visitors use the Site — for example, which pages are most
              popular and how visitors move around the Site. Google Analytics
              collects information such as your approximate location, device,
              and pages viewed. You can learn how Google uses this data at{" "}
              <a
                href="https://policies.google.com/technologies/partner-sites"
                target="_blank"
                rel="noreferrer"
              >
                policies.google.com/technologies/partner-sites
              </a>
              .
            </li>
            <li>
              <strong>Advertising.</strong> We display advertising served by
              Google AdSense. Third-party vendors, including Google, use
              cookies to serve ads based on your prior visits to this Site and
              other websites. Google's use of advertising cookies enables it
              and its partners to serve ads to you based on your visits to this
              Site and/or other sites on the Internet.
            </li>
            <li>
              <strong>Marketing.</strong> We use the Meta (Facebook) Pixel to
              measure the effectiveness of our marketing on Facebook and
              Instagram and to reach people who have shown interest in our
              listings.
            </li>
          </ul>

          <h2>Your advertising choices</h2>
          <p>
            You may opt out of personalized advertising from Google by visiting{" "}
            <a
              href="https://www.google.com/settings/ads"
              target="_blank"
              rel="noreferrer"
            >
              Google Ads Settings
            </a>
            . You can also opt out of the use of cookies by many third-party
            vendors for personalized advertising by visiting{" "}
            <a href="https://www.aboutads.info" target="_blank" rel="noreferrer">
              www.aboutads.info
            </a>{" "}
            or{" "}
            <a
              href="https://youradchoices.com"
              target="_blank"
              rel="noreferrer"
            >
              youradchoices.com
            </a>
            . Most browsers also allow you to refuse or delete cookies through
            their settings; note that some features of the Site may not work
            properly without cookies.
          </p>

          <h2>How we use your information</h2>
          <ul>
            <li>To respond to inquiries and provide our real estate services;</li>
            <li>To operate, maintain, and improve the Site;</li>
            <li>To measure Site usage and the performance of our marketing;</li>
            <li>To display advertising that supports the free use of the Site;</li>
            <li>To comply with legal obligations.</li>
          </ul>

          <h2>Sharing of information</h2>
          <p>
            We do not sell your personal information. We share information only
            with service providers that help us operate the Site and our
            business — such as Google (analytics and advertising), Meta
            (marketing measurement), and form and hosting providers — and where
            required by law. Property inquiries may be shared with the broker,
            agent, or property owner responsible for the listing you asked
            about, so they can respond to you.
          </p>

          <h2>Data retention and security</h2>
          <p>
            We keep personal data only for as long as needed for the purposes
            described above or as required by law, after which it is securely
            deleted. We use reasonable organizational and technical safeguards
            to protect personal data against unauthorized access, disclosure,
            or loss.
          </p>

          <h2>Your rights under the Data Privacy Act</h2>
          <p>
            Under the Data Privacy Act of 2012, you have the right to be
            informed about how your data is processed, to access and correct
            your data, to object to processing, to request erasure or blocking
            of data, and to lodge a complaint with the National Privacy
            Commission (
            <a
              href="https://privacy.gov.ph"
              target="_blank"
              rel="noreferrer"
            >
              privacy.gov.ph
            </a>
            ). To exercise any of these rights, contact us using the details
            below.
          </p>

          <h2>Children's privacy</h2>
          <p>
            The Site is intended for a general audience and is not directed at
            children. We do not knowingly collect personal information from
            children. If you believe a child has provided us personal
            information, please contact us so we can delete it.
          </p>

          <h2>Third-party links</h2>
          <p>
            The Site contains links to third-party websites and services,
            including social media pages and inquiry forms hosted by Google
            Forms. Those services have their own privacy policies, and we
            encourage you to review them.
          </p>

          <h2>Changes to this policy</h2>
          <p>
            We may update this Privacy Policy from time to time. The "Last
            updated" date at the top of this page shows when it was last
            revised. Significant changes will be highlighted on the Site.
          </p>

          <h2>Contact us</h2>
          <p>
            If you have questions about this Privacy Policy or how we handle
            your data, contact us at:
          </p>
          <ul>
            <li>
              Email:{" "}
              <a href="mailto:propeller.realty@gmail.com">
                propeller.realty@gmail.com
              </a>
            </li>
            <li>
              Phone: <a href="tel:+639060012922">+63 906 001 2922</a>
            </li>
            <li>Address: West Capitol Drive, Kapitolyo, Pasig, Philippines</li>
          </ul>
        </div>
      </StaticPageStyle>
    </React.Fragment>
  );
};

export default PrivacyPolicy;
